import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  LayoutDashboard,
  Wallet,
  CalendarRange,
  TrendingUp,
  BellRing,
  Plus,
  Settings,
  HelpCircle,
  Target,
  DollarSign,
  Menu
} from 'lucide-react';

// Import components
import NavItem from './components/NavItem';
import DashboardView from './components/DashboardView';
import OperationsView from './components/OperationsView';
import TrackingView from './components/TrackingView';
import ProjectionsView from './components/ProjectionsView';
import StrategyView from './components/StrategyView';
import FinancialGoalsView from './components/FinancialGoalsView';
import PriceUpdateView from './components/PriceUpdateView';
import SettingsView from './components/SettingsView';

// Import utilities
import { saveToLocalStorage, loadFromLocalStorage, exportAppData, importAppData } from './utils/dataManager';
import { loadFromFirestore, saveToFirestore } from './utils/firebaseManager';
import { auth } from './firebase';
import { signInWithEmailAndPassword, createUserWithEmailAndPassword, signOut, onAuthStateChanged } from 'firebase/auth';

const App = () => {
  const [activeTab, setActiveTab] = useState('dashboard');
  const [user, setUser] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [authMode, setAuthMode] = useState('login'); // 'login' | 'register'
  const [authEmail, setAuthEmail] = useState('');
  const [authPassword, setAuthPassword] = useState('');
  const [authError, setAuthError] = useState('');
  const [authSubmitting, setAuthSubmitting] = useState(false);
  const [syncStatus, setSyncStatus] = useState('idle'); // idle | saving | saved | error
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const saveTimerRef = useRef(null);
  const firestoreLoadedRef = useRef(false);
  const [currency, setCurrency] = useState(() => loadFromLocalStorage('currency', 'USD'));
  const [usdClpRate, setUsdClpRate] = useState(() => loadFromLocalStorage('usdClpRate', 942.50));
  const [currentPrices, setCurrentPrices] = useState(() => loadFromLocalStorage('currentPrices', {
    'ETF S&P 500': 48000,
    'FCI Deuda Local': 1.02,
    'Stock Apple': 195000,
    'ETF Nasdaq': 55000
  }));
  const normalizeOperations = (ops) =>
    ops.map(op => ({
      ...op,
      type: op.type ? op.type.charAt(0).toUpperCase() + op.type.slice(1).toLowerCase() : op.type
    }));

  const loadOperations = () => {
    const stored = loadFromLocalStorage('operations', null);
    // If stored data has only old 2024 dates (sample data), reset to new defaults
    if (stored && stored.length > 0 && stored.every(op => new Date(op.date) < new Date('2025-01-01'))) {
      return null; // triggers default below
    }
    return stored;
  };

  const defaultOperations = [
    {
      id: 1, type: 'Buy', name: 'ETF S&P 500', quantity: 10, price: 45000,
      date: '2025-04-10', assetType: 'ETF'
    },
    {
      id: 2, type: 'Buy', name: 'FCI Deuda Local', quantity: 500000, price: 1.0,
      date: '2025-06-15', assetType: 'FCI'
    },
    {
      id: 3, type: 'Buy', name: 'Stock Apple', quantity: 5, price: 180000,
      date: '2025-08-20', assetType: 'Stock'
    },
    {
      id: 4, type: 'Buy', name: 'ETF Nasdaq', quantity: 8, price: 52000,
      date: '2025-10-05', assetType: 'ETF'
    },
    {
      id: 5, type: 'Buy', name: 'FCI Deuda Local', quantity: 200000, price: 1.0,
      date: '2025-12-01', assetType: 'FCI'
    },
    {
      id: 6, type: 'Sell', name: 'ETF S&P 500', quantity: 3, price: 48000,
      date: '2026-01-20', assetType: 'ETF'
    },
    {
      id: 7, type: 'Buy', name: 'ETF Nasdaq', quantity: 5, price: 54000,
      date: '2026-02-10', assetType: 'ETF'
    },
    {
      id: 8, type: 'Buy', name: 'Stock Apple', quantity: 3, price: 195000,
      date: '2026-03-05', assetType: 'Stock'
    }
  ];

  const [operations, setOperations] = useState(() =>
    normalizeOperations(loadOperations() || defaultOperations)
  );
  const [priceHistory, setPriceHistory] = useState(() => loadFromLocalStorage('priceHistory', {}));
  const [investorProfile, setInvestorProfile] = useState(() => loadFromLocalStorage('investorProfile', 'moderate'));
  const [goals, setGoals] = useState(() => loadFromLocalStorage('goals', [
    {
      id: 1,
      name: "Vacaciones Europa",
      targetAmount: 5000000,
      currentAmount: 1200000,
      deadline: "2025-08-01",
      monthlyContribution: 150000
    },
    {
      id: 2,
      name: "Auto nuevo",
      targetAmount: 8000000,
      currentAmount: 2500000,
      deadline: "2026-03-01",
      monthlyContribution: 200000
    }
  ]));

  // Simulation State
  const [initialCapital, setInitialCapital] = useState(() => loadFromLocalStorage('initialCapital', 15000000));
  const [monthlyContribution, setMonthlyContribution] = useState(() => loadFromLocalStorage('monthlyContribution', 450000));
  const [expectedReturn, setExpectedReturn] = useState(() => loadFromLocalStorage('expectedReturn', 8.5));
  const [horizon, setHorizon] = useState(() => loadFromLocalStorage('horizon', 5));

  // ── Auth state listener ────────────────────────────────────────────────
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      setUser(currentUser);
      setAuthLoading(false);
    });
    return unsubscribe;
  }, []);

  // ── Load from Firestore when user logs in ───────────────────────────────
  useEffect(() => {
    if (!user) {
      firestoreLoadedRef.current = false;
      return;
    }
    firestoreLoadedRef.current = false;
    setSyncStatus('loading');
    loadFromFirestore(user.uid).then((data) => {
      if (data) {
        if (data.operations)      { setOperations(normalizeOperations(data.operations)); saveToLocalStorage('operations', data.operations); }
        if (data.currentPrices)   { setCurrentPrices(data.currentPrices);   saveToLocalStorage('currentPrices', data.currentPrices); }
        if (data.investorProfile) { setInvestorProfile(data.investorProfile); saveToLocalStorage('investorProfile', data.investorProfile); }
        if (data.goals)           { setGoals(data.goals);                    saveToLocalStorage('goals', data.goals); }
        if (data.usdClpRate)      { setUsdClpRate(data.usdClpRate);          saveToLocalStorage('usdClpRate', data.usdClpRate); }
        if (data.currency)        { setCurrency(data.currency);              saveToLocalStorage('currency', data.currency); }
        if (data.simulation) {
          const s = data.simulation;
          if (s.initialCapital !== undefined)      { setInitialCapital(s.initialCapital);           saveToLocalStorage('initialCapital', s.initialCapital); }
          if (s.monthlyContribution !== undefined) { setMonthlyContribution(s.monthlyContribution); saveToLocalStorage('monthlyContribution', s.monthlyContribution); }
          if (s.expectedReturn !== undefined)      { setExpectedReturn(s.expectedReturn);           saveToLocalStorage('expectedReturn', s.expectedReturn); }
          if (s.horizon !== undefined)             { setHorizon(s.horizon);                        saveToLocalStorage('horizon', s.horizon); }
        }
      }
      if (data.priceHistory)    { setPriceHistory(data.priceHistory);      saveToLocalStorage('priceHistory', data.priceHistory); }
      firestoreLoadedRef.current = true;
      setSyncStatus('idle');
    }).catch((err) => {
      console.error('Error cargando datos del usuario:', err);
      firestoreLoadedRef.current = true; // allow saves even on error
      setSyncStatus('error');
    });
  }, [user]); // eslint-disable-line

  // ── Auto-save to localStorage (instant) + Firestore (debounced 1.5s) ────
  const buildPayload = useCallback(() => ({
    operations, currentPrices, investorProfile, goals,
    usdClpRate, currency, priceHistory,
    simulation: { initialCapital, monthlyContribution, expectedReturn, horizon }
  }), [operations, currentPrices, investorProfile, goals, usdClpRate, currency, priceHistory, initialCapital, monthlyContribution, expectedReturn, horizon]);

  useEffect(() => {
    // Instant localStorage write
    saveToLocalStorage('currency', currency);
    saveToLocalStorage('usdClpRate', usdClpRate);
    saveToLocalStorage('currentPrices', currentPrices);
    saveToLocalStorage('operations', operations);
    saveToLocalStorage('investorProfile', investorProfile);
    saveToLocalStorage('goals', goals);
    saveToLocalStorage('initialCapital', initialCapital);
    saveToLocalStorage('monthlyContribution', monthlyContribution);
    saveToLocalStorage('expectedReturn', expectedReturn);
    saveToLocalStorage('horizon', horizon);
    saveToLocalStorage('priceHistory', priceHistory);

    // Debounced Firestore write (only when authenticated AND initial load is complete)
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    if (!user || !firestoreLoadedRef.current) return;
    setSyncStatus('saving');
    saveTimerRef.current = setTimeout(() => {
      saveToFirestore(user.uid, buildPayload())
        .then(() => setSyncStatus('saved'))
        .catch(() => setSyncStatus('error'));
    }, 1500);
  }, [operations, currentPrices, investorProfile, goals, usdClpRate, currency, priceHistory, initialCapital, monthlyContribution, expectedReturn, horizon, user, buildPayload]);

  // Data management functions
  const handleExportData = () => {
    const appState = {
      operations,
      currentPrices,
      investorProfile,
      goals,
      initialCapital,
      monthlyContribution,
      expectedReturn,
      horizon,
      usdClpRate,
      currency
    };
    exportAppData(appState);
  };

  const handleImportData = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    importAppData(file)
      .then((importedData) => {
        // Update all state with imported data
        if (importedData.operations !== undefined) setOperations(normalizeOperations(importedData.operations));
        if (importedData.currentPrices !== undefined) setCurrentPrices(importedData.currentPrices);
        if (importedData.investorProfile !== undefined) setInvestorProfile(importedData.investorProfile);
        if (importedData.goals !== undefined) setGoals(importedData.goals);
        if (importedData.simulationSettings) {
          const sim = importedData.simulationSettings;
          if (sim.initialCapital !== undefined) setInitialCapital(sim.initialCapital);
          if (sim.monthlyContribution !== undefined) setMonthlyContribution(sim.monthlyContribution);
          if (sim.expectedReturn !== undefined) setExpectedReturn(sim.expectedReturn);
          if (sim.horizon !== undefined) setHorizon(sim.horizon);
        }
        if (importedData.usdClpRate !== undefined) setUsdClpRate(importedData.usdClpRate);
        if (importedData.currency !== undefined) setCurrency(importedData.currency);

        alert('Data imported successfully!');
      })
      .catch((error) => {
        alert('Error importing data: ' + error.message);
      });

    // Reset file input
    event.target.value = '';
  };

  const handlePriceUpdated = useCallback((assetName, price) => {
    const today = new Date().toISOString().split('T')[0];
    setPriceHistory(prev => {
      const existing = prev[assetName] || [];
      const filtered = existing.filter(e => e.date !== today);
      return { ...prev, [assetName]: [...filtered, { date: today, price }] };
    });
  }, []);

  const handleNavClick = (tab) => {
    setActiveTab(tab);
    setIsMobileMenuOpen(false);
  };

  const formatCurrency = (val) => {
    if (currency === 'USD') {
      return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(val / usdClpRate);
    }
    return new Intl.NumberFormat('es-CL', { style: 'currency', currency: 'CLP' }).format(val);
  };

  const renderContent = () => {
    switch (activeTab) {
      case 'dashboard':
        return <DashboardView 
          currency={currency} 
          formatCurrency={formatCurrency} 
          usdClpRate={usdClpRate}
          operations={operations}
          currentPrices={currentPrices}
          investorProfile={investorProfile}
          priceHistory={priceHistory}
        />;
      case 'operations':
        return <OperationsView 
          formatCurrency={formatCurrency}
          operations={operations}
          setOperations={setOperations}
          currentPrices={currentPrices}
          usdClpRate={usdClpRate}
        />;
      case 'tracking':
        return <TrackingView 
          formatCurrency={formatCurrency}
          operations={operations}
          currentPrices={currentPrices}
        />;
      case 'projections':
        return (
          <ProjectionsView
            initialCapital={initialCapital} setInitialCapital={setInitialCapital}
            monthlyContribution={monthlyContribution} setMonthlyContribution={setMonthlyContribution}
            expectedReturn={expectedReturn} setExpectedReturn={setExpectedReturn}
            horizon={horizon} setHorizon={setHorizon}
            formatCurrency={formatCurrency}
          />
        );
      case 'strategy':
        return <StrategyView 
          operations={operations}
          currentPrices={currentPrices}
          usdClpRate={usdClpRate}
          formatCurrency={formatCurrency}
          investorProfile={investorProfile}
        />;
      case 'goals':
        return <FinancialGoalsView
          formatCurrency={formatCurrency}
          goals={goals}
          setGoals={setGoals}
        />;
      case 'prices':
        return <PriceUpdateView 
          currentPrices={currentPrices}
          setCurrentPrices={setCurrentPrices}
          usdClpRate={usdClpRate}
          setUsdClpRate={setUsdClpRate}
          operations={operations}
          formatCurrency={formatCurrency}
          onPriceUpdated={handlePriceUpdated}
        />;
      case 'settings':
        return <SettingsView 
          investorProfile={investorProfile}
          setInvestorProfile={setInvestorProfile}
          onExportData={handleExportData}
          onImportData={handleImportData}
        />;
      default:
        return <DashboardView 
          currency={currency} 
          formatCurrency={formatCurrency} 
          usdClpRate={usdClpRate}
          operations={operations}
          currentPrices={currentPrices}
          investorProfile={investorProfile}
          priceHistory={priceHistory}
        />;
    }
  };

  if (authLoading) return (
    <div className="min-h-screen bg-[#f7f9fc] flex items-center justify-center">
      <div className="w-8 h-8 border-4 border-[#041627] border-t-transparent rounded-full animate-spin" />
    </div>
  );

  const handleAuthSubmit = async (e) => {
    e.preventDefault();
    setAuthError('');
    setAuthSubmitting(true);
    try {
      if (authMode === 'login') {
        await signInWithEmailAndPassword(auth, authEmail, authPassword);
      } else {
        await createUserWithEmailAndPassword(auth, authEmail, authPassword);
      }
    } catch (err) {
      const messages = {
        'auth/user-not-found': 'No existe una cuenta con ese correo.',
        'auth/wrong-password': 'Contraseña incorrecta.',
        'auth/invalid-credential': 'Correo o contraseña incorrectos.',
        'auth/email-already-in-use': 'Ya existe una cuenta con ese correo.',
        'auth/weak-password': 'La contraseña debe tener al menos 6 caracteres.',
        'auth/invalid-email': 'El formato del correo no es válido.',
      };
      setAuthError(messages[err.code] || 'Ocurrió un error. Intentá de nuevo.');
    } finally {
      setAuthSubmitting(false);
    }
  };

  if (!user) return (
    <div className="min-h-screen bg-[#041627] flex items-center justify-center">
      <div className="bg-white rounded-2xl shadow-2xl p-10 w-full max-w-sm">
        <div className="text-center mb-8">
          <h1 className="text-2xl font-bold text-[#041627] mb-1">Equity Reserve</h1>
          <p className="text-[10px] text-[#44474c] uppercase tracking-widest font-semibold">Private Portfolio</p>
        </div>

        <div className="flex bg-[#e6e8eb] rounded-full p-1 mb-8">
          <button
            onClick={() => { setAuthMode('login'); setAuthError(''); }}
            className={`flex-1 py-1.5 rounded-full text-xs font-bold transition-all ${
              authMode === 'login' ? 'bg-white shadow-sm text-[#041627]' : 'text-[#44474c]'
            }`}
          >Iniciar sesión</button>
          <button
            onClick={() => { setAuthMode('register'); setAuthError(''); }}
            className={`flex-1 py-1.5 rounded-full text-xs font-bold transition-all ${
              authMode === 'register' ? 'bg-white shadow-sm text-[#041627]' : 'text-[#44474c]'
            }`}
          >Registrarse</button>
        </div>

        <form onSubmit={handleAuthSubmit} className="space-y-4">
          <div>
            <label className="block text-xs font-semibold text-[#44474c] mb-1.5">Correo electrónico</label>
            <input
              type="email"
              required
              value={authEmail}
              onChange={(e) => setAuthEmail(e.target.value)}
              placeholder="tu@correo.com"
              className="w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#041627] focus:border-transparent"
            />
          </div>
          <div>
            <label className="block text-xs font-semibold text-[#44474c] mb-1.5">Contraseña</label>
            <input
              type="password"
              required
              value={authPassword}
              onChange={(e) => setAuthPassword(e.target.value)}
              placeholder="Mínimo 6 caracteres"
              className="w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#041627] focus:border-transparent"
            />
          </div>

          {authError && (
            <p className="text-xs text-red-500 bg-red-50 rounded-lg px-3 py-2">{authError}</p>
          )}

          <button
            type="submit"
            disabled={authSubmitting}
            className="w-full bg-[#041627] text-white py-3 rounded-xl text-sm font-semibold hover:opacity-90 active:scale-95 transition-all disabled:opacity-50"
          >
            {authSubmitting ? 'Procesando…' : authMode === 'login' ? 'Ingresar' : 'Crear cuenta'}
          </button>
        </form>
      </div>
    </div>
  );

  return (
    <div className="flex min-h-screen bg-[#f7f9fc] text-[#191c1e] font-sans">
      {/* Mobile sidebar backdrop */}
      {isMobileMenuOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setIsMobileMenuOpen(false)}
        />
      )}

      {/* SideNavBar */}
      <aside className={`fixed left-0 top-0 h-screen w-64 border-r border-slate-200 bg-slate-50 flex flex-col p-4 z-50 overflow-y-auto transition-transform duration-300 ease-in-out ${isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full'} lg:translate-x-0`}>
        <div className="mb-10 px-2">
          <h1 className="text-xl font-bold tracking-tight text-[#041627] font-headline">Equity Reserve</h1>
          <p className="text-[10px] text-[#44474c] uppercase tracking-widest font-semibold mt-1">Private Portfolio</p>
        </div>

        <nav className="flex-1 space-y-2">
          <NavItem active={activeTab === 'dashboard'} onClick={() => handleNavClick('dashboard')} icon={<LayoutDashboard size={20} />} label="Dashboard" />
          <NavItem active={activeTab === 'operations'} onClick={() => handleNavClick('operations')} icon={<Wallet size={20} />} label="Operations" />
          <NavItem active={activeTab === 'tracking'} onClick={() => handleNavClick('tracking')} icon={<CalendarRange size={20} />} label="Monthly Tracking" />
          <NavItem active={activeTab === 'projections'} onClick={() => handleNavClick('projections')} icon={<TrendingUp size={20} />} label="Scenarios" />
          <NavItem active={activeTab === 'strategy'} onClick={() => handleNavClick('strategy')} icon={<BellRing size={20} />} label="Strategy & Alerts" />
          <NavItem active={activeTab === 'goals'} onClick={() => handleNavClick('goals')} icon={<Target size={20} />} label="Financial Goals" />
          <NavItem active={activeTab === 'prices'} onClick={() => handleNavClick('prices')} icon={<DollarSign size={20} />} label="Update Prices" />
        </nav>

        <div className="mt-auto space-y-2 pt-4 border-t border-slate-200">
          <button onClick={() => handleNavClick('operations')} className="w-full bg-[#041627] text-white py-2.5 rounded-lg text-sm font-semibold flex items-center justify-center gap-2 mb-4 hover:opacity-90 active:scale-95 transition-all">
            <Plus size={16} /> Add Operation
          </button>
          <NavItem icon={<Settings size={20} />} label="Settings" onClick={() => handleNavClick('settings')} />
          <NavItem icon={<HelpCircle size={20} />} label="Support" />
        </div>
      </aside>

      {/* Main Area */}
      <div className="lg:ml-64 flex-1 min-w-0">
        {/* TopNavBar */}
        <header className="sticky top-0 z-40 bg-white/80 backdrop-blur-md border-b border-slate-100 h-16 px-4 lg:px-8 flex justify-between items-center">
          <div className="flex items-center gap-2 lg:gap-6">
            <button
              className="lg:hidden p-2 hover:bg-slate-100 rounded-lg transition-colors"
              onClick={() => setIsMobileMenuOpen(true)}
              aria-label="Abrir menú"
            >
              <Menu size={20} />
            </button>
            <h2 className="text-sm font-bold text-[#041627] font-headline capitalize hidden sm:block">{activeTab} Overview</h2>
            <div className="flex bg-[#e6e8eb] rounded-full p-1 h-8 items-center">
              <button
                onClick={() => setCurrency('USD')}
                className={`px-4 py-1 rounded-full text-[10px] font-bold transition-all ${currency === 'USD' ? 'bg-white shadow-sm text-[#041627]' : 'text-[#44474c]'}`}
              >
                USD
              </button>
              <button
                onClick={() => setCurrency('CLP')}
                className={`px-4 py-1 rounded-full text-[10px] font-bold transition-all ${currency === 'CLP' ? 'bg-white shadow-sm text-[#041627]' : 'text-[#44474c]'}`}
              >
                CLP
              </button>
            </div>
            <span className="text-xs text-[#44474c] border-l border-slate-200 pl-6 font-medium hidden lg:block">USD/CLP: {usdClpRate.toFixed(2)}</span>
          </div>

          <div className="flex items-center gap-2 lg:gap-4">
            {syncStatus === 'loading' && (
              <span className="text-[10px] text-[#44474c] flex items-center gap-1">
                <span className="w-1.5 h-1.5 bg-blue-400 rounded-full animate-pulse inline-block" />
                Cargando…
              </span>
            )}
            {syncStatus === 'saving' && (
              <span className="text-[10px] text-[#44474c] flex items-center gap-1">
                <span className="w-1.5 h-1.5 bg-amber-400 rounded-full animate-pulse inline-block" />
                Syncing…
              </span>
            )}
            {syncStatus === 'saved' && (
              <span className="text-[10px] text-emerald-600 flex items-center gap-1">
                <span className="w-1.5 h-1.5 bg-emerald-500 rounded-full inline-block" />
                Synced
              </span>
            )}
            {syncStatus === 'error' && (
              <span className="text-[10px] text-red-500 flex items-center gap-1">
                <span className="w-1.5 h-1.5 bg-red-500 rounded-full inline-block" />
                Sync error
              </span>
            )}
            <div className="text-right hidden md:block">
              <p className="text-xs font-bold text-[#041627]">{user.email}</p>
              <p className="text-[10px] text-[#44474c]">Portfolio privado</p>
            </div>
            <div className="h-10 w-10 rounded-full overflow-hidden border-2 border-[#e6e8eb] bg-[#041627] flex items-center justify-center text-white text-sm font-bold">
              {user.email[0].toUpperCase()}
            </div>
            <button
              onClick={() => signOut(auth)}
              title="Sign out"
              className="text-[10px] text-[#44474c] hover:text-red-500 transition-colors px-2 py-1 rounded-lg hover:bg-red-50"
            >
              Sign out
            </button>
          </div>
        </header>

        <main className="p-4 md:p-6 lg:p-8 animate-in fade-in duration-500">
          {renderContent()}
        </main>
      </div>
    </div>
  );
};

export default App;